import { Component, computed, inject, linkedSignal, resource, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { firstValueFrom } from "rxjs";
import { Ticket } from "src/app/_models";
import { ProjectService, TicketService } from "src/app/_services";
import { StatusSelectComponent } from "../../_shared/status-select/status-select.component";
import { statusLabel } from "../../_shared/status-select/status-grouping";
import { TicketSearchItem } from "../types";

@Component({
  selector: "ticket-grid-status-editor",
  template: `
    <hawk-status-select
      class="bm-join-item w-[300px]"
      [statuses]="projectStatuses.value()"
      [ngModel]="statusId()"
      (ngModelChange)="statusId.set($event)"
      [loading]="projectStatuses.isLoading()"
      [required]="true"
    />
    <button
      class="flex-none bm-join-item bm-btn bm-btn-primary bm-btn-square bm-tooltip"
      [attr.data-tip]="'Speichern'"
      (click)="save()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'save']" />
    </button>
    <button
      class="flex-none bm-join-item bm-btn bm-btn-square bm-tooltip"
      [attr.data-tip]="'Verwerfen'"
      (click)="cancel()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'times']" />
    </button>
  `,
  imports: [FormsModule, StatusSelectComponent, FontAwesomeModule],
  host: {
    class: "bm-join",
  },
})
export class TicketGridStatusEditorComponent implements ICellEditorAngularComp {
  protected projectService = inject(ProjectService);
  protected ticketService = inject(TicketService);

  protected params = signal<ICellEditorParams<TicketSearchItem>>(null);
  protected data = computed(() => this.params()?.data);
  protected projectId = computed(() => this.data()?.project_id);
  protected projectStatuses = resource({
    params: this.projectId,
    loader: async ({ params }) => {
      return firstValueFrom(this.projectService.getStatuses(params));
    },
  });
  protected isSaving = signal(false);
  protected isLoading = this.projectStatuses.isLoading;

  protected statusId = linkedSignal<number>(() => this.data()?.status_hash?.status_id);
  protected value = linkedSignal<string>(() => this.params()?.value);
  protected picker = viewChild(StatusSelectComponent);

  public agInit(params: ICellEditorParams) {
    this.params.set(params);
  }

  public refresh(params: ICellEditorParams) {
    this.params.set(params);
    return true;
  }

  public getValue() {
    return this.value();
  }

  public afterGuiAttached(): void {
    this.picker().open();
  }

  public isPopup(): boolean {
    return true; // editor doesn't fit into a cell, so it should be a popup
  }

  public async save() {
    this.isSaving.set(true);
    const id = this.data().id;
    const status = this.projectStatuses.value().find((it) => it.id === this.statusId());
    const params: Pick<Ticket, "status_id"> = {
      status_id: this.statusId(),
    };
    firstValueFrom(this.ticketService.updateTicket(String(id), params))
      .then(() => {
        // Zellwert = Anzeigename inkl. Kategorie, wie status_text der Ticketsuche
        this.value.set(statusLabel(status));
        this.params().stopEditing();
      })
      .catch(() => {
        // Handle error, e.g., show a notification
        console.error("Failed to save ticket status");
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }

  public cancel() {
    this.params().stopEditing();
  }

  // getPopupPosition?(): "over" | "under" | undefined {
  //   throw new Error("Method not implemented.");
  // }
  // isCancelBeforeStart?(): boolean {
  //   throw new Error("Method not implemented.");
  // }
  // isCancelAfterEnd?(): boolean {
  //   throw new Error("Method not implemented.");
  // }
  // focusIn?(): void {
  //   throw new Error("Method not implemented.");
  // }
  // focusOut?(): void {
  //   throw new Error("Method not implemented.");
  // }
}
